import React from 'react';
import { Link } from 'react-router-dom';
import {
  Badge, Button, ErrorState, SectionCard, SkeletonText,
} from '@parsina/framework';
import { Earth, Pencil } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { gql } from '../../lib/api';
import { CAPABILITY_FIELDS, CLINIC_SWITCHES, countryPath } from '../../constants/countryProfile';
import { CLINIC_COUNTRY_QUERY } from '../Countries/queries';
import {
  CeilingNote, ColumnValue, CountryCode, EditorLabel, NotReadBadge, ValueText, countryErrorMessage,
  useColumnLabel, useCountryName,
} from '../Countries/shared';
import CountrySettingsDialog from './CountrySettingsDialog';

/** The clinic switch that took a capability away, if one did. */
const switchFor = (field) => CLINIC_SWITCHES.find((s) => s.takes.includes(field))?.field || null;

/**
 * «تنظیمات کشور» on a clinic's record (PM-0218): the clinic's jurisdiction, what
 * it may do there — its country's profile with this clinic's overrides applied,
 * the deployment ceiling beside each capability — its own switches and its
 * وزارت بهداشت ids, editable here with a note.
 *
 * Its OWN region, with its own query and states: the subscription record above
 * renders exactly as before whether or not the country registry answers, and a
 * registry that is down costs this card alone.
 */
export default function CountrySettingsCard({ clinicId }) {
  const { t, formatDate, formatList } = useTranslation();
  const countryName = useCountryName();
  const columnLabel = useColumnLabel();

  const [view, setView] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [editing, setEditing] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    // Through a promise even if the transport throws synchronously: whatever
    // goes wrong here is this card's error state, never the record's.
    Promise.resolve()
      .then(() => gql(CLINIC_COUNTRY_QUERY, { clinicId }))
      .then((d) => {
        if (!d?.clinicCountrySettings) throw new Error(t('countries.clinicCard.error'));
        setView(d.clinicCountrySettings);
      })
      .catch((e) => setError(countryErrorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [clinicId, t]);

  React.useEffect(() => { load(); }, [load]);

  let body;
  if (loading) {
    body = (
      <div role="status" aria-busy="true" aria-label={t('countries.clinicCard.loading')} data-state="loading" data-testid="clinic-country-loading">
        <SkeletonText lines={4} />
      </div>
    );
  } else if (error) {
    body = (
      <ErrorState
        title={t('countries.clinicCard.error')}
        description={error}
        onRetry={load}
        retryLabel={t('common.actions.retry')}
        data-testid="clinic-country-error"
      />
    );
  } else {
    const { settings, effective, countryProfile, deployment } = view;
    // Outside the Iranian market Iranian e-prescription does not exist here, so
    // neither does its switch nor the وزارت بهداشت ids it alone uses.
    const intl = deployment?.market === 'intl';
    const switches = CLINIC_SWITCHES.filter(({ field }) => !(intl && field === 'erxEnabled'));
    body = (
      <div className="flex flex-col gap-4" data-state="content">
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="clinic-country-country">
          <span className="text-xs text-muted-foreground">{t('countries.clinicCard.country')}</span>
          <span className="font-semibold">{countryName(view.country)}</span>
          <CountryCode code={view.country} />
          {!view.profileFound ? <Badge variant="warning">{t('countries.detail.noRow')}</Badge> : null}
          <Link to={countryPath(view.country)} className="ms-auto text-xs text-primary hover:underline" data-testid="clinic-country-rules-link">
            {t('countries.clinicCard.seeRules')}
          </Link>
        </div>

        <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2" aria-label={t('countries.clinicCard.effective')} data-testid="clinic-country-effective">
          {CAPABILITY_FIELDS.map((spec) => {
            const sw = switchFor(spec.field);
            const takenAway = sw && settings[sw] === false;
            return (
              <div key={spec.field} className="min-w-0" data-testid={`clinic-country-cap-${spec.field}`}>
                <dt className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  {t(`countries.fields.${spec.field}`)}
                  <NotReadBadge field={spec.field} />
                </dt>
                <dd className="mt-1 font-semibold">
                  <ColumnValue spec={spec} row={effective} merged deployment={deployment} testId={`clinic-country-value-${spec.field}`} />
                </dd>
                {takenAway ? (
                  <dd className="mt-0.5 text-xs" data-testid={`clinic-country-override-${spec.field}`}>
                    {/* A status the clinic chose, not an error: the warning chip. */}
                    <Badge variant="warning">{t('countries.clinicCard.takenAway')}</Badge>
                    {countryProfile ? (
                      <span className="text-muted-foreground">
                        {' · '}
                        {t('countries.clinicCard.countrySays')}
                        {' '}
                        <ColumnValue spec={spec} row={countryProfile} merged deployment={deployment} testId={`clinic-country-says-${spec.field}`} />
                      </span>
                    ) : null}
                  </dd>
                ) : null}
                <dd className="mt-0.5"><CeilingNote spec={spec} value={effective[spec.field]} deployment={deployment} /></dd>
              </div>
            );
          })}
        </dl>

        <div className="grid grid-cols-1 gap-3 border-t border-border pt-3 text-sm md:grid-cols-2" data-testid="clinic-country-settings">
          {switches.map(({ field }) => (
            <div key={field} className="min-w-0">
              <div className="text-xs text-muted-foreground">{t(`countries.clinicCard.switches.${field}`)}</div>
              <div className="font-semibold" data-testid={`clinic-country-switch-${field}`}>
                {settings[field] === false ? t('countries.clinicCard.off') : t('countries.clinicCard.follow')}
              </div>
            </div>
          ))}
          {intl ? (
            <div className="min-w-0 md:col-span-2" data-testid="clinic-country-intl">
              <Badge variant="warning">{t('countries.clinicCard.intlNote')}</Badge>
            </div>
          ) : ['mohmeOrgId', 'universityId'].map((field) => (
            <div key={field} className="min-w-0">
              <div className="text-xs text-muted-foreground">{t(`countries.clinicCard.ids.${field}`)}</div>
              <div data-testid={`clinic-country-id-${field}`}>
                {settings[field]
                  ? <code {...LTR_TEXT} className="font-mono text-[13px] font-semibold">{settings[field]}</code>
                  : <span className="text-muted-foreground">{t('countries.clinicCard.ids.notRegistered')}</span>}
              </div>
            </div>
          ))}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
              {t('countries.clinicCard.calendar')}
              <NotReadBadge field="calendar" />
            </div>
            <div className="font-semibold">
              {settings.calendar
                ? <ValueText spec={{ field: 'calendar', type: 'enum' }} value={settings.calendar} />
                : t('countries.clinicCard.follow')}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
          {settings.updatedAt ? (
            <span className="inline-flex items-center gap-1.5" data-testid="clinic-country-updated">
              {t('countries.detail.updated', { date: formatDate(settings.updatedAt) })}
              <EditorLabel by={settings.updatedBy} />
            </span>
          ) : <span>{t('countries.clinicCard.neverSet')}</span>}
          <Button
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={() => setEditing(true)}
            data-testid="clinic-country-edit-open"
          >
            <Pencil size={14} aria-hidden />
            {t('countries.clinicCard.edit')}
          </Button>
        </div>

        {view.history?.length ? (
          <ul className="flex flex-col gap-1.5 text-xs" aria-label={t('countries.clinicCard.history')} data-testid="clinic-country-history">
            {view.history.slice(0, 5).map((h) => (
              <li key={h.id} className="flex flex-wrap items-center gap-1.5">
                <span>{h.at ? formatDate(h.at) : t('common.value.none')}</span>
                <EditorLabel by={h.actor} />
                <span className="text-muted-foreground">{formatList(h.changedFields.map((f) => columnLabelForSetting(f, t, columnLabel)))}</span>
                {h.note ? <span dir="auto" className="truncate" title={h.note}>— {h.note}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  return (
    <SectionCard
      id="country"
      title={t('countries.clinicCard.title')}
      sub={t('countries.clinicCard.sub')}
      icon={Earth}
      data-testid="clinic-country"
    >
      {body}
      {view ? (
        <CountrySettingsDialog
          open={editing}
          onOpenChange={setEditing}
          clinicId={clinicId}
          settings={view.settings}
          countryLabel={countryName(view.country)}
          market={view.deployment?.market}
          // The write is done once this runs. Its fresh view when the BFF could
          // read one; else the stored settings now and the rest read again.
          onSaved={(saved) => {
            if (saved?.view) { setView(saved.view); return; }
            if (saved?.settings) setView((v) => ({ ...v, settings: saved.settings }));
            load();
          }}
        />
      ) : null}
    </SectionCard>
  );
}

/** A setting's name in the history line: a switch or an id by its own label. */
function columnLabelForSetting(column, t, columnLabel) {
  const map = {
    erx_enabled: 'countries.clinicCard.switches.erxEnabled',
    sms_enabled: 'countries.clinicCard.switches.smsEnabled',
    insurance_enabled: 'countries.clinicCard.switches.insuranceEnabled',
    mohme_org_id: 'countries.clinicCard.ids.mohmeOrgId',
    university_id: 'countries.clinicCard.ids.universityId',
    calendar: 'countries.clinicCard.calendar',
  };
  return map[column] ? t(map[column]) : columnLabel(column);
}
